/// <reference types="@netfront/ekardo-content-library/dist/types/window" />
import 'backpack.css';

import '@netfront/ui-library/dist/esm/css/index.css';
import '@netfront/ui-library/dist/css/global.css';
import '@netfront/ui-library/dist/css/helpers.css';
import '@netfront/ui-library/dist/css/typography.css';

import '@netfront/ekardo-content-library/dist/esm/css/index.css';

import '../styles/components.css';
import '../styles/fonts.css';
import '../styles/globals.css';
import '../styles/theme.css';
import '../styles/utils.css';

import { useCallback, useEffect, useRef, useState } from 'react';

import { ApolloClientsProvider } from '@netfront/apollo-providers';
import { useMediaQuery } from '@netfront/common-library';
import { ContentPageProvider, useGoogleAnalytics } from '@netfront/ekardo-content-library';
import { useIdentitySiteUrls, useUser } from '@netfront/gelada-identity-library';
import { CookieConsent, GoogleCaptchaProvider } from '@netfront/ui-library';
import LogRocket from 'logrocket';
import { AppProps } from 'next/app';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { Toaster } from 'react-hot-toast';

import { FacebookPixel } from '../components';
import { QUICKCODES_COOKIE_CONSENT_MESSAGE } from '../constants';
import { PermissionProvider, CachingEntitiesProvider, DashboardProvider, UserProvider, PlanProvider } from '../contexts';
import { getGoogleAnalyticsUserId, getGtagConfigParams } from '../utils/getGtagConfigParams';
import { IUseUserType } from '../utils/userType';

function AppInner({ Component, pageProps }: Pick<AppProps, 'Component' | 'pageProps'>) {
  const googleAnalyticsMeasurementId = String(process.env.REACT_APP_GOOGLE_ANALYTICS);
  const isGoogleAnalyticsDebugMode = String(process.env.REACT_APP_GOOGLE_ANALYTICS_DEBUG_MODE).toUpperCase() === 'Y';

  const { trackPageView, trackUser } = useGoogleAnalytics();
  const { events } = useRouter();
  const { getUser } = useUser();

  const isMobile = useMediaQuery({
    size: 'small',
  });

  const [hasKanziTool, setHasKanziTool] = useState<boolean>(false);
  

  const user = getUser();

  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
    LogRocket.init('sfzpuh/quickcodes');

    if (user) {
      const { id: userId, firstName, lastName, email } = user as unknown as IUseUserType;

      LogRocket.identify(String(userId), {
        name: `${firstName} ${lastName}`,
        email: email,
      });
    }
  }

  // The last gtag config sent, and whether this page has seen a user with an id
  const gtagConfigRef = useRef<string | undefined>(undefined);
  const hasHadGoogleAnalyticsUserIdRef = useRef<boolean>(false);
  const hasFiredInitialPageView = useRef<boolean>(false);

  // Brings the gtag config's User-ID up to date. It reads the user cookie each time, so a logout that doesn't reload the page
  // clears the User-ID before the next page view
  const updateGtagConfig = useCallback(() => {
    const userId = getGoogleAnalyticsUserId(getUser()?.id);

    if (userId) {
      hasHadGoogleAnalyticsUserIdRef.current = true;
    }

    const params = getGtagConfigParams({
      hasSignedOut: hasHadGoogleAnalyticsUserIdRef.current && !userId,
      isDebugMode: isGoogleAnalyticsDebugMode,
      userId,
    });
    const serializedParams = JSON.stringify(params);

    if (serializedParams === gtagConfigRef.current) {
      return;
    }

    gtagConfigRef.current = serializedParams;
    window.gtag('config', googleAnalyticsMeasurementId, params);
  }, [getUser, googleAnalyticsMeasurementId, isGoogleAnalyticsDebugMode]);

  useEffect(() => {
    updateGtagConfig();
  }, [updateGtagConfig]);

  // Page views are sent whether or not the user is logged in; the shared hook adds the User-ID only when there is an access token
  useEffect(() => {
    if (!hasFiredInitialPageView.current) {
      trackPageView(window.location.pathname);
      trackUser();
      hasFiredInitialPageView.current = true;
    }
  }, [trackPageView, trackUser]);

  useEffect(() => {
    const handleRouteChange = () => {
      updateGtagConfig();
      trackPageView(window.location.pathname);
    };

    events.on('routeChangeComplete', handleRouteChange);

    return () => {
      events.off('routeChangeComplete', handleRouteChange);
    };
  }, [events, trackPageView, updateGtagConfig]);


  useEffect(() => {
    if (!hasKanziTool) return;

    const kanziTools = [...document.getElementsByClassName('c-kanzi')];

    if (kanziTools.length === 0) return;

    kanziTools.forEach((kanziTool) => {
      const kanziToolElement = kanziTool as HTMLElement;

      if (isMobile) {
        kanziToolElement.style.display = 'none';
      } else {
        kanziToolElement.style.display = 'block';
      }
    });

  }, [isMobile, hasKanziTool]);

  // we have to wait until the kanzi script has injected the html before we can determine whether to hide it
  useEffect(() => {
    const interval = setInterval(() => {
      const kanziTools = [...document.getElementsByClassName('c-kanzi')];

      if (kanziTools.length > 0) {
        setHasKanziTool(true);
        clearInterval(interval);
      }
    }, 500);

    return () => clearInterval(interval);
  }, []);

  return (
    <div id="root">
      <Script
        src={`https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsMeasurementId}`}
        strategy="afterInteractive"
      />
      <Script id="gtag-init" strategy="afterInteractive">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
        `}
      </Script>

      <GoogleCaptchaProvider siteKey={String(process.env.REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY ?? '')}>
        <CachingEntitiesProvider>
          <PlanProvider>
            <UserProvider>
              <PermissionProvider>
                <DashboardProvider>
                  <div>
                    <Toaster
                      position="top-center"
                      reverseOrder={false}
                      toastOptions={{
                        duration: 3000,
                      }}
                    />
                  </div>
                  <FacebookPixel />
                  <CookieConsent cookieConsentMessage={QUICKCODES_COOKIE_CONSENT_MESSAGE} />
                  {
                    <ContentPageProvider>
                      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                      <Component {...pageProps} />
                    </ContentPageProvider>
                  }
                </DashboardProvider>
              </PermissionProvider>
            </UserProvider>
          </PlanProvider>
        </CachingEntitiesProvider>
      </GoogleCaptchaProvider>
    </div>
  );
}

function App({ Component, pageProps }: AppProps) {
  const router = useRouter();
  const { query: { projectId } } = router;
  const { push } = useRouter();
  const { getLoginUrl } = useIdentitySiteUrls({
    environment: process.env.REACT_APP_ENVIRONMENT,
    port: process.env.REACT_APP_IDENTITY_SITE_LOCAL_PORT,
  });

  const onUnauthenticated = () => {
    if (typeof window === 'undefined') {
      return;
    }
    void push(getLoginUrl());
  };

  return (
      <ApolloClientsProvider
        options={{
          projectId: projectId ? String(projectId) : undefined,
          onUnauthenticated,
          endpointSource: {
            source: 'environment variables'
          },
        }}
      >
        <AppInner Component={Component} pageProps={pageProps} />
      </ApolloClientsProvider>
  );
}

// eslint-disable-next-line import/no-default-export
export default App;
